/* ==========================================================================
     <nosferatu-story>
     Two states are tracked separately and never conflated:
       memberAuthorized — server-rendered from Shopify customer tags. The only
                          thing that may change commerce behaviour.
       memberView       — presentation only. Set by memberAuthorized OR by an
                          Inner Circle UTM/query signal from a member email.
                          A link can never be authorization.
     ========================================================================== */
  const NOS_MEMBER_SIGNAL_VALUES = [
    '1',
    'true',
    'ic_member',
    'active_ic_member',
    'active_inner_circle_member'
  ]

  /* Revision 2, item 10 / revision 4, item 7. A hard cap on the blood trail:
     this many smear nodes are created once and reused round-robin, plus
     exactly one puddle node. The layer's cost is fixed no matter how long or
     how fast the pointer moves. */
  const NOS_SMEAR_POOL = 18

  /* Revision 7, item 2. Droplets are back ALONGSIDE the smears — round 4
     replaced them wholesale and the client wants the earlier feel, which was
     blood that falls as well as blood that wipes. Same pooled discipline: a
     fixed number of nodes created once and reused round-robin, so the layer's
     cost is still a constant. 18 + 7 + 1 = 26 nodes against round 4's 15; all
     of them animate transform and opacity only. */
  const NOS_DROP_POOL = 7

  /* Revision 4, item 9. The three display states, in cycle order. NIGHT is the
     page as built; STILLED is night with the showpiece motion parked; DAY is
     the Ink Inverse palette. Day borrows the stilled motion level rather than
     inventing a fourth combination. */
  const NOS_DISPLAY_STATES = ['night', 'stilled', 'day']

  /* REVISION 7, ITEM 3. Three original pieces on one control, in cycle order,
     with OFF as the fourth stop: organ -> heartbeat -> music box -> off. All
     synthesized; no recordings, no external assets, no borrowed melody.
     `NOS_AUDIO_LOOKAHEAD` is how far ahead of the playhead the scheduler
     writes; it must stay comfortably larger than the scheduler's own tick so a
     throttled or backgrounded tab still lands its notes before they are due. */
  const NOS_TUNES = ['organ', 'heart', 'lament']
  const NOS_AUDIO_TICK = 240
  const NOS_AUDIO_LOOKAHEAD = 1.5

  /* D natural minor, three answering lines. The phrase index picks one, so the
     organ says something different every phrase instead of repeating one bar
     of eight notes forever. Pedals move with them. */
  const NOS_ORGAN_LINES = [
    [146.83, 174.61, 220.00, 233.08, 220.00, 174.61, 164.81, 146.83],
    [146.83, 164.81, 174.61, 196.00, 220.00, 196.00, 174.61, 130.81],
    [174.61, 220.00, 233.08, 261.63, 233.08, 220.00, 196.00, 174.61]
  ]
  const NOS_ORGAN_PEDALS = [73.42, 55.00, 58.27]

  /* The music box: sparse falling figures in the same key, each a bell rather
     than a note. Pairs of [scale degree index, beat offset]. */
  const NOS_LAMENT_SCALE = [587.33, 523.25, 466.16, 440.00, 392.00, 349.23, 293.66, 261.63]
  const NOS_LAMENT_FIGURES = [
    [[0, 0], [2, 1.6], [4, 3.0], [3, 5.1], [6, 6.9]],
    [[1, 0], [3, 1.4], [5, 2.9], [4, 4.8], [7, 6.6]],
    [[2, 0], [0, 1.9], [3, 3.4], [5, 5.0], [6, 7.1]]
  ]

  class NosferatuStory extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return
      this.dataset.ready = 'true'

      const query = new URLSearchParams(window.location.search)

      // --- presentation-only member signals from campaign email links -------
      const signals = [query.get('ic_member'), query.get('utm_content')]
        .filter(Boolean)
        .map((value) => value.toLowerCase().replaceAll('-', '_').replaceAll(' ', '_'))
      const memberEmailView = signals.some((value) => NOS_MEMBER_SIGNAL_VALUES.includes(value))
      if (memberEmailView) this.classList.add('is-member-view')

      this.memberAuthorized = this.dataset.memberAuthorized === 'true'
      this.memberView = this.classList.contains('is-member-view')
      this.audioEnabled = this.dataset.audioEnabled === 'true'

      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      this.dataset.motion = this.prefersReducedMotion ? 'paused' : 'playing'

      /* REVISION 7, ITEM 1. The dagger was scoped to this element, so it died
         at the crypt: the proof section, the card game, the FAQ and the footer
         all showed the native arrow. The blade now hangs off <body>, and this
         is the switch that turns that on — the same reason `data-nos-display`
         lives on <html>: it is the one node every part of the page can see.

         It is written unconditionally. The pointer and reduced-motion gates
         are media queries in the stylesheet, which is the right place for
         them: they have to answer live when the visitor plugs in a mouse or
         changes the system setting, and a value read once at init cannot. */
      document.documentElement.dataset.nosCursor = 'dagger'

      // --- quest state ------------------------------------------------------
      this.keyStorageKey = `nosferatu-coffin-keys:${window.location.pathname}`
      this.foundKeys = new Set()
      try {
        const stored = JSON.parse(window.sessionStorage.getItem(this.keyStorageKey) || '[]')
        stored.forEach((id) => this.foundKeys.add(String(id)))
      } catch (error) {
        this.foundKeys.clear()
      }

      // Revision 3, item B. The gate now has TWO doors onto it — "Accept the
      // invitation" in the hero and "Open the crypt" at the finale — so the
      // answer has to be remembered, or a visitor who already said which they
      // are gets asked again halfway down the page. Stored beside the key hunt,
      // same sessionStorage, same pathname scope, same failure mode: if storage
      // is unavailable the gate simply asks again rather than breaking.
      //
      // REVISION 5, ITEM 1. Only the literal string 'true' counts as an answer.
      // The sign-in door writes 'pending' while the theme's login lightbox is
      // open in place; a visitor who abandons that popup — or who reloads
      // mid-sign-in — never chose a door, so a stored 'pending' is discarded
      // here and the gate asks again. See gateIsAnswered().
      this.gateStorageKey = `nosferatu-gate-answered:${window.location.pathname}`
      this.gateIntent = 'chapter'
      this.gatePendingSignIn = false
      try {
        this.gateAnswered = window.sessionStorage.getItem(this.gateStorageKey) === 'true'
        if (!this.gateAnswered) window.sessionStorage.removeItem(this.gateStorageKey)
      } catch (error) {
        this.gateAnswered = false
      }

      // --- element handles --------------------------------------------------
      this.liveRegion = this.querySelector('[data-nos-live]')
      this.motionButton = this.querySelector('[data-nos-motion]')
      this.motionLabel = this.querySelector('[data-nos-motion-label]')
      this.displayState = this.querySelector('[data-nos-display-state]')
      this.reliquaryDisc = this.querySelector('[data-nos-reliquary]')
      this.organButton = this.querySelector('[data-nos-organ]')
      this.organLabel = this.querySelector('[data-nos-organ-label]')
      // revision 7, item 3 — which of the three tunes is sounding
      this.organTune = this.querySelector('[data-nos-organ-tune]')
      this.tuneIndex = 0
      this.audioAwake = false
      this.tally = this.querySelector('[data-nos-tally]')
      this.keyCounts = Array.from(this.querySelectorAll('[data-nos-key-count]'))
      this.ascentReadout = this.querySelector('[data-nos-ascent]')
      this.acceptButton = this.querySelector('[data-nos-accept]')
      this.gate = this.querySelector('[data-nos-gate]')
      this.gatePanel = this.querySelector('[data-nos-gate-panel]')
      this.chapterOne = this.querySelector('#chapter-i')
      this.letterButton = this.querySelector('[data-nos-letter]')
      this.letterInk = this.querySelector('[data-nos-letter-ink]')
      this.letterResult = this.querySelector('[data-nos-letter-result]')
      this.letterPrompt = this.querySelector('[data-nos-letter-prompt]')
      this.letterMessages = Array.from(this.querySelectorAll('[data-nos-letter-message]'))
        .map((node) => node.textContent.trim())
        .filter(Boolean)
      this.letterIndex = 0
      this.letterOpened = false
      this.materialScope = this.querySelector('[data-nos-material-scope]')
      this.vault = this.querySelector('[data-nos-vault]')
      this.card = this.querySelector('[data-nos-card]')
      this.openCryptButton = this.querySelector('[data-nos-open-crypt]')
      this.buyBoxSlot = this.querySelector('[data-nos-buybox-slot]')
      this.logoutLink = this.querySelector('[data-nos-logout]')
      this.loops = Array.from(this.querySelectorAll('[data-nos-loop]'))
      this.reveals = Array.from(this.querySelectorAll('.nos-reveal'))
      this.cryptSection = this.querySelector('.nos-crypt')
      this.celebration = this.querySelector('[data-nos-celebrate]')

      this.onScroll = this.onScroll.bind(this)
      this.dockBuyBox = this.dockBuyBox.bind(this)
      this.endCelebration = this.endCelebration.bind(this)
      this.rescueLoginModal = this.rescueLoginModal.bind(this)

      // Revision 4, item 9. Must run after the element handles are collected
      // (it writes the chip's label and state word) and before anything that
      // reads `motionAllowed`.
      this.initDisplay()

      this.wireControls()
      this.wireSignIn()
      this.wireGate()
      this.wireLetter()
      this.wireScents()
      this.wirePlate()
      this.wireMaterialToggle()
      this.wireKeys()
      this.wireVault()
      this.wireCrypt()
      this.wireDrips()
      this.observeScenes()

      // The theme's global login popup lives inside the featured-product
      // section; it has to be lifted out before it can ever be opened. Runs on
      // load because that section is parsed after this one.
      if (document.readyState === 'complete') {
        window.setTimeout(this.rescueLoginModal, 0)
      } else {
        window.addEventListener('load', this.rescueLoginModal, { once: true })
      }

      // Veils are applied HERE, at init, and never baked into the markup: if
      // this script never runs, every image is simply visible.
      this.applyVeils()

      window.addEventListener('scroll', this.onScroll, { passive: true })
      this.onScroll()

      // The prospect buy box is only relocated for non-members. For an
      // authenticated member the prospect card is not rendered server-side at
      // all, so there is nothing to dock.
      if (!this.memberAuthorized) {
        if (document.readyState === 'complete') {
          window.setTimeout(this.dockBuyBox, 0)
        } else {
          window.addEventListener('load', this.dockBuyBox, { once: true })
        }
      } else {
        // The later section has not been parsed yet at this point, so wait.
        this.hideProspectBuyBox = this.hideProspectBuyBox.bind(this)
        if (document.readyState === 'complete') {
          window.setTimeout(this.hideProspectBuyBox, 0)
        } else {
          window.addEventListener('load', this.hideProspectBuyBox, { once: true })
        }
        // Members have nothing to unlock; the crypt stands open for them.
        this.classList.add('is-crypt-open')
      }

      if (query.get('show_join') === '1') {
        this.classList.add('is-returned-from-logout')
        window.setTimeout(() => this.cryptSection?.scrollIntoView({
          behavior: this.prefersReducedMotion ? 'auto' : 'smooth',
          block: 'center'
        }), 140)
      }

      this.classList.add('is-ready')
    }

    disconnectedCallback() {
      this.sceneObserver?.disconnect()
      window.removeEventListener('scroll', this.onScroll)
      window.removeEventListener('load', this.dockBuyBox)
      window.removeEventListener('load', this.rescueLoginModal)
      if (this.onDripMove) window.removeEventListener('pointermove', this.onDripMove)
      if (this.onDripDown) window.removeEventListener('pointerdown', this.onDripDown)
      if (this.onDripUp) {
        window.removeEventListener('pointerup', this.onDripUp)
        window.removeEventListener('pointercancel', this.onDripUp)
      }
      window.clearTimeout(this.puddleTimer)
      window.clearTimeout(this.puddleDryTimer)
      window.clearTimeout(this.puddleResetTimer)
      window.clearInterval(this.puddleInterval)
      if (this.dripFrame) window.cancelAnimationFrame(this.dripFrame)
      this.dripFrame = 0
      document.removeEventListener('pointerdown', this.endCelebration)
      document.removeEventListener('keydown', this.endCelebration)
      window.clearTimeout(this.celebrationTimer)
      this.stopAudio()
      this.stopHeartbeat()
    }

    announce(message) {
      if (this.liveRegion && message) this.liveRegion.textContent = message
    }

    /* --------------------------------------------------- motion and display */
    wireControls() {
      this.motionButton?.addEventListener('click', () => this.cycleDisplay())
      this.organButton?.addEventListener('click', () => this.cycleAudio())
      this.syncAudioControl()
    }

    /* REVISION 4, ITEM 9 — one control, three states.

       "Still the night" was a two-state toggle and is now a cycle:

           NIGHT (full motion) -> STILLED -> DAY -> NIGHT

       Three decisions worth keeping:

       · DAY IMPLIES A STILLED LEVEL OF MOTION. `data-motion` still carries
         exactly two values, so every motion rule written in rounds 1-3 keeps
         working untouched and there is no fourth "day with parallax" state to
         reason about.
       · THE STATE IS WRITTEN TO <html>, not to this element. The gate, the ring
         lightbox, the blood layer and the theme's login popup are all portalled
         to <body>, and the membership-proof section is a different section
         entirely. `document.documentElement` is the only node all five can see.
       · `aria-pressed` IS GONE. It is a boolean, and this control has three
         states; the state now lives in the button's accessible name, where it
         can actually say which one is live.

       The first state comes from sessionStorage, so the choice survives a
       reload; failing that, a reduced-motion visitor starts STILLED and
       everyone else starts NIGHT. Reduced motion never selects DAY on its own —
       it is a palette, not an accommodation, and choosing it for somebody would
       be guessing. */
    initDisplay() {
      this.displayStorageKey = `nosferatu-display:${window.location.pathname}`
      let stored = null
      try {
        stored = window.sessionStorage.getItem(this.displayStorageKey)
      } catch (error) {
        stored = null
      }
      const initial = NOS_DISPLAY_STATES.indexOf(stored) > -1
        ? stored
        : (this.prefersReducedMotion ? 'stilled' : 'night')
      this.applyDisplay(initial, { persist: false, announce: false })
    }

    applyDisplay(state, options) {
      const config = options || {}
      const next = NOS_DISPLAY_STATES.indexOf(state) > -1 ? state : 'night'
      const wasPlaying = this.dataset.motion !== 'paused'
      this.display = next

      /* ======================= REVISION 5, ITEM 7 =======================
         Round 4's rule — "day borrows the stilled level of motion" — is
         REVOKED at the client's request. The three states are now:

           NIGHT    full motion, dark palette
           STILLED  becalmed: loops parked on their posters, ambient only
           DAY      full motion, light palette

         `data-motion` still carries exactly two values, so every motion rule
         written in rounds 1-4 keeps working untouched; what changed is which
         states map to which value. STILLED is now the only state that pauses.

         prefers-reduced-motion still overrides all three: it forces
         `data-motion="paused"` whatever palette is chosen, on top of the
         `animation: none !important` the media query already applies. Day is a
         palette, not a motion preference, so choosing it must never start
         animation for somebody who asked for none. */
      this.dataset.motion = (next === 'stilled' || this.prefersReducedMotion) ? 'paused' : 'playing'
      this.dataset.display = next
      document.documentElement.dataset.nosDisplay = next

      // The label is the ACTION the next click will take; the state word is
      // where the page currently is. Both, so neither is a riddle.
      const actions = {
        night: this.dataset.motionPauseLabel,
        stilled: this.dataset.motionDayLabel,
        day: this.dataset.motionPlayLabel
      }
      const states = {
        night: this.dataset.stateNightLabel,
        stilled: this.dataset.stateStilledLabel,
        day: this.dataset.stateDayLabel
      }
      if (this.motionLabel) this.motionLabel.textContent = actions[next] || ''
      if (this.displayState) this.displayState.textContent = states[next] || ''

      if (this.dataset.motion !== 'paused') {
        // night OR day: the film loops resume where they stand
        this.syncLoops()
      } else {
        // Loops PAUSE (they keep their frame / poster), they are not removed,
        // and any celebration in flight ends now. What does NOT stop is the
        // ambient layer — grain, candle flicker, the breathing hero glow and
        // the film bed's slow flutter all keep going in CSS, so a stilled page
        // is quiet rather than dead. Only prefers-reduced-motion silences those.
        this.loops.forEach((video) => video.pause())
        if (wasPlaying) this.endCelebration()
      }
      // the blood trail survives every state but goes quieter when becalmed
      this.dripLayer?.classList.toggle('is-stilled', this.dataset.motion === 'paused')

      if (config.persist !== false) {
        try {
          window.sessionStorage.setItem(this.displayStorageKey, next)
        } catch (error) {
          // The cycle still works without storage; it just resets on reload.
        }
      }
      if (config.announce !== false) {
        this.announce(`${this.dataset.displayAnnounce || ''} ${states[next] || next}`.trim())
      }
    }

    cycleDisplay() {
      const index = NOS_DISPLAY_STATES.indexOf(this.display || 'night')
      this.applyDisplay(NOS_DISPLAY_STATES[(index + 1) % NOS_DISPLAY_STATES.length], {
        persist: true,
        announce: true
      })
    }

    get motionAllowed() {
      return this.dataset.motion !== 'paused'
    }

    /* ----------------------------------------------------------------- audio
       REVISION 7, ITEM 3 — the ambience rebuilt as a scheduler with three tunes.

       WHAT WAS WRONG. Rounds 1-6 called `playOrganPhrase()` on a 12,800ms
       setInterval. The eight-note line ran out at +10.8s and the pedal drone
       decayed to nothing by +12.2s, so measured at the master gain there was a
       2.7-SECOND effectively-silent hole in every 12.8s cycle — 24.8% of the
       running time — landing on a perfect clock, always in the same place, in
       front of the same eight notes. That is exactly what the client heard as
       "ends quickly, then a long silence".

       WHAT REPLACES IT. Three things, and the third is the one that matters:

       1. A LOOKAHEAD SCHEDULER. A 240ms tick writes every phrase that falls due
          within the next 1.5s. setInterval drift, a throttled background tab
          and a slow frame can no longer put a hole in the sound, because the
          notes were committed to the audio clock long before they were due.
       2. PHRASES THAT OVERLAP RATHER THAN ABUT. Every piece returns the STEP to
          the next phrase's onset, and every piece's own tail is longer than
          that step. The next phrase therefore begins while the current one is
          still ringing. There is no moment when nothing is sounding.
       3. VARIATION. Each piece rotates its material by phrase index, so the
          same bar never comes round twice in succession.

       All three pieces are original and synthesized here; there are no
       recordings, no external assets and no borrowed melody. Nothing whatever
       is constructed before an explicit click on the toggle — `ensureAudio()`
       is reachable from that click and from nowhere else.

       THE ROUTING, and why it is two gains deep:

           piece nodes -> voiceGain -> masterGain -> destination
           cues        ------------->  masterGain -> destination

       `voiceGain` belongs to ONE tune. Switching tunes ramps the old voice down
       over 0.9s while the new one is already scheduling into a fresh voice — a
       real crossfade, not a stop and a start. The Chapter V heartbeat cue and
       the door creak hang off `masterGain` instead, so changing the tune under
       them never chops a cue in half. `masterGain` is what OFF collapses, and
       OFF still closes the context outright: nothing survives it. */
    ensureAudio() {
      if (this.audioContext) return this.audioContext
      const Ctx = window.AudioContext || window.webkitAudioContext
      if (!Ctx) return null
      /* Mobile fix (2026-09-01): iOS treats Web Audio as "sound effects" and
         mutes it while the ringer switch is on silent. Declaring the session
         as media playback (Safari 16.4+) exempts it, like a music player.
         No-op on every other platform. */
      try { if (navigator.audioSession) navigator.audioSession.type = 'playback' } catch (e) { /* ignore */ }
      this.audioContext = new Ctx()
      /* Older-iOS unlock: one silent sample started inside the activating
         gesture moves a suspended/interrupted context to running. */
      try {
        const unlockSrc = this.audioContext.createBufferSource()
        unlockSrc.buffer = this.audioContext.createBuffer(1, 1, 22050)
        unlockSrc.connect(this.audioContext.destination)
        unlockSrc.start(0)
      } catch (e) { /* ignore */ }
      this.masterGain = this.audioContext.createGain()
      this.masterGain.gain.setValueAtTime(0.0001, this.audioContext.currentTime)
      this.masterGain.connect(this.audioContext.destination)
      return this.audioContext
    }

    /* ONE control, four stops: off -> organ -> heartbeat -> music box -> off.
       This is the same shape the display chip took in round 4, for the same
       reason — a second button for "which tune" would be a second thing to
       find, in a rail that is deliberately two chips wide. */
    async cycleAudio() {
      if (!this.audioEnabled) return
      if (!this.audioAwake) {
        this.tuneIndex = 0
        await this.startAudio()
      } else if (this.tuneIndex < NOS_TUNES.length - 1) {
        this.tuneIndex += 1
        this.swapVoice()
      } else {
        this.stopAudio()
      }
      this.syncAudioControl()
    }

    async startAudio() {
      const ctx = this.ensureAudio()
      if (!ctx) return
      await ctx.resume()
      const now = ctx.currentTime
      this.masterGain.gain.cancelScheduledValues(now)
      this.masterGain.gain.setValueAtTime(0.0001, now)
      this.masterGain.gain.exponentialRampToValueAtTime(0.22, now + 1.4)
      this.audioAwake = true
      this.phraseCount = 0
      this.openVoice()
      this.nextPhraseAt = ctx.currentTime + 0.06
      this.scheduleAhead()
      window.clearInterval(this.audioClock)
      this.audioClock = window.setInterval(() => this.scheduleAhead(), NOS_AUDIO_TICK)
      if (this.cryptVisible) this.startHeartbeat()
    }

    stopAudio() {
      window.clearInterval(this.audioClock)
      this.audioClock = null
      this.audioAwake = false
      this.stopHeartbeat()
      if (!this.audioContext || !this.masterGain) return
      const ctx = this.audioContext
      const now = ctx.currentTime
      this.masterGain.gain.cancelScheduledValues(now)
      this.masterGain.gain.setValueAtTime(Math.max(0.0001, this.masterGain.gain.value), now)
      this.masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.7)
      window.setTimeout(() => { try { ctx.close() } catch (error) { /* already closed */ } }, 900)
      this.audioContext = null
      this.masterGain = null
      this.voiceGain = null
    }

    /* Per-tune trim so the three pieces sit at the same apparent loudness: the
       music box is all transient and reads louder than it measures, the
       heartbeat is nearly all sub-bass and reads quieter. */
    openVoice() {
      const ctx = this.audioContext
      if (!ctx) return
      const trim = { organ: 1, heart: 1.15, lament: 0.8 }[NOS_TUNES[this.tuneIndex]] || 1
      this.voiceGain = ctx.createGain()
      this.voiceGain.gain.setValueAtTime(trim, ctx.currentTime)
      this.voiceGain.connect(this.masterGain)
    }

    swapVoice() {
      const ctx = this.audioContext
      if (!ctx) return
      const retiring = this.voiceGain
      if (retiring) {
        const now = ctx.currentTime
        retiring.gain.cancelScheduledValues(now)
        retiring.gain.setValueAtTime(retiring.gain.value, now)
        retiring.gain.linearRampToValueAtTime(0, now + 0.9)
        // Everything the old tune ever scheduled is downstream of this gain, so
        // one disconnect retires all of it however far ahead it was written.
        window.setTimeout(() => { try { retiring.disconnect() } catch (error) { /* gone */ } }, 1300)
      }
      this.phraseCount = 0
      this.openVoice()
      this.nextPhraseAt = ctx.currentTime + 0.05
      this.scheduleAhead()
    }

    /* The lookahead. Everything due inside the next 1.5s is committed now. */
    scheduleAhead() {
      if (!this.audioAwake || !this.audioContext || !this.voiceGain) return
      const ctx = this.audioContext
      let guard = 0
      while (this.nextPhraseAt < ctx.currentTime + NOS_AUDIO_LOOKAHEAD && guard < 8) {
        guard += 1
        const at = Math.max(this.nextPhraseAt, ctx.currentTime + 0.03)
        const tune = NOS_TUNES[this.tuneIndex]
        const step = tune === 'heart'
          ? this.phraseHeart(at)
          : (tune === 'lament' ? this.phraseLament(at) : this.phraseOrgan(at))
        this.nextPhraseAt = at + step
        this.phraseCount += 1
      }
    }

    /* --- (a) THE ORGAN, improved -----------------------------------------
       Still a slow original line in D minor voiced with harmonic ranks the way
       a pipe organ stacks its stops. What is new: the line and the pedal both
       rotate through three variants, the notes are spaced so the melody fills
       the whole step, and the pedal sustains 3.2s PAST the step so consecutive
       phrases lap over one another. Returns the step to the next onset. */
    phraseOrgan(at) {
      const ctx = this.audioContext
      const out = this.voiceGain
      const index = this.phraseCount % NOS_ORGAN_LINES.length
      const line = NOS_ORGAN_LINES[index]
      const pedal = NOS_ORGAN_PEDALS[index]
      const step = 8.4
      const tail = 11.6

      const droneFilter = ctx.createBiquadFilter()
      droneFilter.type = 'lowpass'
      droneFilter.frequency.setValueAtTime(760, at)
      const droneGain = ctx.createGain()
      droneGain.gain.setValueAtTime(0.0001, at)
      droneGain.gain.exponentialRampToValueAtTime(0.13, at + 1.8)
      droneGain.gain.setValueAtTime(0.13, at + tail - 3.4)
      droneGain.gain.exponentialRampToValueAtTime(0.0001, at + tail)
      droneFilter.connect(droneGain)
      droneGain.connect(out)
      ;[1, 2].forEach((rank) => {
        const osc = ctx.createOscillator()
        osc.type = 'triangle'
        osc.frequency.setValueAtTime(pedal * rank, at)
        osc.connect(droneFilter)
        osc.start(at)
        osc.stop(at + tail + 0.2)
      })

      line.forEach((frequency, note) => {
        const noteAt = at + (note * (step / line.length))
        const filter = ctx.createBiquadFilter()
        filter.type = 'lowpass'
        filter.frequency.setValueAtTime(1900, noteAt)
        const envelope = ctx.createGain()
        envelope.gain.setValueAtTime(0.0001, noteAt)
        envelope.gain.exponentialRampToValueAtTime(0.072, noteAt + 0.34)
        envelope.gain.setValueAtTime(0.072, noteAt + 0.86)
        envelope.gain.exponentialRampToValueAtTime(0.0001, noteAt + 1.7)
        filter.connect(envelope)
        envelope.connect(out)
        ;[1, 2, 3, 4].forEach((rank, rankIndex) => {
          const osc = ctx.createOscillator()
          const rankGain = ctx.createGain()
          osc.type = rankIndex % 2 === 0 ? 'triangle' : 'sine'
          osc.frequency.setValueAtTime(frequency * rank, noteAt)
          osc.detune.setValueAtTime(rankIndex === 3 ? 5 : 0, noteAt)
          rankGain.gain.setValueAtTime(1 / (rank * 1.5), noteAt)
          osc.connect(rankGain)
          rankGain.connect(filter)
          osc.start(noteAt)
          osc.stop(noteAt + 1.8)
        })
      })
      return step
    }

    /* --- (b) THE HEARTBEAT --------------------------------------------------
       The thing the client half-heard under Chapter V, grown into a piece of
       its own: a slow lub-dub at ~55bpm over a sub-bass pedal and a soft band
       of room tone, with one distant minor dyad leaning in each phrase. Eight
       beats to a phrase; the pedal and the room tone run 2.4s past the step. */
    phraseHeart(at) {
      const ctx = this.audioContext
      const out = this.voiceGain
      const beat = 1.09                   // ~55bpm
      const beats = 8
      const step = beat * beats
      const tail = step + 2.4

      // sub-bass pedal, alternating tonic and minor sixth
      const subGain = ctx.createGain()
      subGain.gain.setValueAtTime(0.0001, at)
      // 0.8s, not 1.6s: on a tune change this pedal is the incoming piece's
      // floor, and the outgoing voice is only given 0.9s to fade. A slower
      // attack than that leaves a measurable dip in the crossfade.
      subGain.gain.exponentialRampToValueAtTime(0.09, at + 0.8)
      subGain.gain.setValueAtTime(0.09, at + tail - 2.2)
      subGain.gain.exponentialRampToValueAtTime(0.0001, at + tail)
      subGain.connect(out)
      const sub = ctx.createOscillator()
      sub.type = 'sine'
      sub.frequency.setValueAtTime(this.phraseCount % 2 ? 36.71 : 27.50, at)
      sub.connect(subGain)
      sub.start(at)
      sub.stop(at + tail + 0.2)

      // room tone: one cached noise buffer, band-limited and looped
      const room = ctx.createBufferSource()
      room.buffer = this.noiseBuffer(ctx)
      room.loop = true
      const roomFilter = ctx.createBiquadFilter()
      roomFilter.type = 'lowpass'
      roomFilter.frequency.setValueAtTime(320, at)
      const roomGain = ctx.createGain()
      roomGain.gain.setValueAtTime(0.0001, at)
      roomGain.gain.exponentialRampToValueAtTime(0.05, at + 2)
      roomGain.gain.setValueAtTime(0.05, at + tail - 2.4)
      roomGain.gain.exponentialRampToValueAtTime(0.0001, at + tail)
      room.connect(roomFilter)
      roomFilter.connect(roomGain)
      roomGain.connect(out)
      room.start(at)
      room.stop(at + tail + 0.1)

      for (let index = 0; index < beats; index += 1) {
        this.thump(at + (index * beat), 0.17, out)
        this.thump(at + (index * beat) + 0.3, 0.12, out)
      }

      // one distant dyad, a minor third apart, placed off the beat
      const dyadAt = at + (beat * (this.phraseCount % 3 === 0 ? 2.5 : 5.5))
      ;[174.61, 207.65].forEach((frequency, voice) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.setValueAtTime(frequency, dyadAt)
        gain.gain.setValueAtTime(0.0001, dyadAt)
        gain.gain.exponentialRampToValueAtTime(voice ? 0.018 : 0.026, dyadAt + 0.9)
        gain.gain.exponentialRampToValueAtTime(0.0001, dyadAt + 3.4)
        osc.connect(gain)
        gain.connect(out)
        osc.start(dyadAt)
        osc.stop(dyadAt + 3.5)
      })
      return step
    }

    /* One low thump: a sine dropping through the bottom of hearing. */
    thump(at, level, out) {
      const ctx = this.audioContext
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(58, at)
      osc.frequency.exponentialRampToValueAtTime(27, at + 0.22)
      gain.gain.setValueAtTime(0.0001, at)
      gain.gain.exponentialRampToValueAtTime(level, at + 0.04)
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.34)
      osc.connect(gain)
      gain.connect(out)
      osc.start(at)
      osc.stop(at + 0.38)
    }

    /* --- (c) THE MUSIC BOX --------------------------------------------------
       A sparse lament in the same D minor: five bells to a phrase on an
       irregular pulse, over a pad so quiet it reads as the room rather than as
       a chord. Bells ring 2.6s, well past the step, so the phrases dovetail. */
    phraseLament(at) {
      const ctx = this.audioContext
      const out = this.voiceGain
      const step = 8.6
      const figure = NOS_LAMENT_FIGURES[this.phraseCount % NOS_LAMENT_FIGURES.length]
      const drop = this.phraseCount % 2 ? 0.5 : 1      // every other phrase an octave down

      /* The pad is what stops a SPARSE piece reading as a broken one. Five
         bells over 8.6s leave real holes between them, so the pad has to be
         audible in its own right (0.052, not 0.035) and it has to be up fast
         (1.1s, not 2.4s) — both because the gaps between bells are short and
         because on a tune change this is the incoming floor against the
         outgoing voice's 0.9s fade. It still sits far enough under the bells
         to read as the room rather than as a chord. */
      const padGain = ctx.createGain()
      padGain.gain.setValueAtTime(0.0001, at)
      padGain.gain.exponentialRampToValueAtTime(0.052, at + 1.1)
      padGain.gain.setValueAtTime(0.052, at + step - 0.4)
      padGain.gain.exponentialRampToValueAtTime(0.0001, at + step + 2.6)
      padGain.connect(out)
      ;[73.42, 110.00].forEach((frequency) => {
        const osc = ctx.createOscillator()
        osc.type = 'triangle'
        osc.frequency.setValueAtTime(frequency, at)
        osc.detune.setValueAtTime(4, at)
        osc.connect(padGain)
        osc.start(at)
        osc.stop(at + step + 2.8)
      })

      figure.forEach(([degree, offset]) => {
        this.bell(at + offset, NOS_LAMENT_SCALE[degree] * drop, 0.09, out)
      })
      return step
    }

    /* A struck bell: a sine carrier with two inharmonic partials above it, each
       decaying at its own rate. Three oscillators is the whole instrument. */
    bell(at, frequency, level, out) {
      const ctx = this.audioContext
      ;[[1, 1, 2.6], [2.76, 0.3, 1.4], [5.4, 0.12, 0.7]].forEach(([ratio, amp, decay]) => {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.setValueAtTime(frequency * ratio, at)
        gain.gain.setValueAtTime(0.0001, at)
        gain.gain.exponentialRampToValueAtTime(level * amp, at + 0.014)
        gain.gain.exponentialRampToValueAtTime(0.0001, at + decay)
        osc.connect(gain)
        gain.connect(out)
        osc.start(at)
        osc.stop(at + decay + 0.05)
      })
    }

    /* Two seconds of noise, filled once and reused by every source that wants
       it — the door creak builds its own short burst, but the room tone would
       otherwise allocate a fresh multi-second buffer on every phrase. */
    noiseBuffer(ctx) {
      if (this.cachedNoise && this.cachedNoise.sampleRate === ctx.sampleRate) return this.cachedNoise
      const length = Math.floor(ctx.sampleRate * 2)
      const buffer = ctx.createBuffer(1, length, ctx.sampleRate)
      const data = buffer.getChannelData(0)
      for (let i = 0; i < length; i += 1) data[i] = (Math.random() * 2 - 1) * 0.5
      this.cachedNoise = buffer
      return buffer
    }

    /* The chip says both things the display chip says: what the next press will
       do, and which tune is sounding now. `aria-pressed` stays a truthful
       boolean — it answers "is the ambience on", which is exactly a boolean —
       and the tune name lives in the state span beside it. */
    syncAudioControl() {
      if (!this.organButton) return
      const tune = this.audioAwake ? NOS_TUNES[this.tuneIndex] : null
      const actions = {
        null: this.dataset.audioWakeLabel,
        organ: this.dataset.audioHeartLabel,
        heart: this.dataset.audioLamentLabel,
        lament: this.dataset.audioSleepLabel
      }
      const states = {
        null: this.dataset.tuneOffLabel,
        organ: this.dataset.tuneOrganLabel,
        heart: this.dataset.tuneHeartLabel,
        lament: this.dataset.tuneLamentLabel
      }
      this.organButton.setAttribute('aria-pressed', this.audioAwake ? 'true' : 'false')
      if (this.organLabel) this.organLabel.textContent = actions[String(tune)] || ''
      if (this.organTune) this.organTune.textContent = states[String(tune)] || ''
    }

    /* A dry hinge-creak: band-passed noise whose centre frequency slides, plus
       a low wooden knock at the end of the swing. */
    playDoorCreak() {
      if (!this.audioAwake || !this.audioContext || !this.masterGain) return
      const ctx = this.audioContext
      const now = ctx.currentTime + 0.02
      const source = ctx.createBufferSource()
      source.buffer = this.noiseBuffer(ctx)
      source.loop = true
      const band = ctx.createBiquadFilter()
      band.type = 'bandpass'
      band.Q.setValueAtTime(14, now)
      band.frequency.setValueAtTime(300, now)
      band.frequency.exponentialRampToValueAtTime(1150, now + 0.85)
      band.frequency.exponentialRampToValueAtTime(520, now + 1.4)
      const gain = ctx.createGain()
      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(0.3, now + 0.12)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 1.45)
      source.connect(band)
      band.connect(gain)
      gain.connect(this.masterGain)
      source.start(now)
      source.stop(now + 1.5)

      const knock = ctx.createOscillator()
      const knockGain = ctx.createGain()
      knock.type = 'sine'
      knock.frequency.setValueAtTime(96, now + 1.35)
      knock.frequency.exponentialRampToValueAtTime(44, now + 1.62)
      knockGain.gain.setValueAtTime(0.0001, now + 1.35)
      knockGain.gain.exponentialRampToValueAtTime(0.34, now + 1.39)
      knockGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.75)
      knock.connect(knockGain)
      knockGain.connect(this.masterGain)
      knock.start(now + 1.35)
      knock.stop(now + 1.8)
    }

    /* THE CHAPTER V CUE, unchanged in when it fires and in what gates it. It
       is an interaction cue, not ambience: it hangs off `masterGain` rather
       than the voice, so rotating tunes underneath it never chops it in half,
       and it survives every tune change. The one addition is the guard on the
       heartbeat TUNE — when the ambience is already a heartbeat, laying a
       second one over it at a different tempo reads as a fault, so the cue
       stands down and the piece itself carries the moment. */
    startHeartbeat() {
      if (!this.audioAwake || this.heartbeatTimer) return
      if (NOS_TUNES[this.tuneIndex] === 'heart') return
      const beat = () => this.playHeartbeat()
      beat()
      this.heartbeatTimer = window.setInterval(beat, 1350)
    }

    stopHeartbeat() {
      window.clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }

    playHeartbeat() {
      if (!this.audioAwake || !this.audioContext || !this.masterGain) return
      const ctx = this.audioContext
      const base = ctx.currentTime + 0.02
      ;[0, 0.31].forEach((offset, index) => {
        const at = base + offset
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.setValueAtTime(62, at)
        osc.frequency.exponentialRampToValueAtTime(30, at + 0.2)
        gain.gain.setValueAtTime(0.0001, at)
        gain.gain.exponentialRampToValueAtTime(index === 0 ? 0.2 : 0.14, at + 0.035)
        gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.3)
        osc.connect(gain)
        gain.connect(this.masterGain)
        osc.start(at)
        osc.stop(at + 0.34)
      })
    }

    /* --------------------------------------------------------------- sign-in
       REVISION 3, ITEM A — every sign-in control on the page, not just the one.

       Round 2 fixed the gate anchor. The audit for this round found four more
       affordances that invite a sign-in, and each was broken in its own way:

         · the hero's "already of the Circle? sign in" line — a bare <p>. Not a
           link at all, so nothing could have worked. Now a real anchor.
         · the gate's "I am of the Circle" anchor — fixed in round 2, kept.
         · the crypt gate's member door (new this round, item B) — same anchor,
           swapped return_url.
         · the proof section's "The Circle knows you" dialog link and its
           exclusives invitation — see nosferatu-membership-proof.liquid.

       Every one of them is a REAL anchor to /account/login with a return_url,
       and every one of them stops the click propagating so the theme's
       document-level interceptor in snippets/product-info.liquid cannot cancel
       the navigation. That is the deliberate, uniform choice for controls we
       author: an anchor always navigates, whether or not the theme's
       #fj-login-modal happens to be on the page (it is emitted only when the
       shopper is logged out AND the product shows member benefits, so it is not
       something to depend on).

       The controls we do NOT author — the theme header's account icon and the
       buy box's own "Already a member? Log In" — are deliberately left to open
       #fj-login-modal, because in-place is the better experience there and the
       theme owns those. rescueLoginModal() guarantees the modal is reachable
       and item C guarantees it is legible.

       ======================= REVISION 4, ITEM 8 =========================
       That last paragraph is now the rule for ALL of them, ours included.
       Round 3 chose navigation everywhere; the client wants the lightbox.
       Reversing it turned out to be a deletion, not an addition:

         The theme's document-level handler in snippets/product-info.liquid
         already opens #fj-login-modal for any click on a link to
         /account/login. Round 3's fix was a single `stopPropagation()` that
         starved that handler. Removing it hands the click straight back to
         the theme's own open mechanism — which is exactly what "via the
         theme's own open mechanism" asks for, with no second implementation
         of a modal to keep in step.

       THE FALLBACK IS STRUCTURAL, NOT A CATCH BLOCK. In
       `snippets/product-info.liquid` the modal sits inside a Liquid `if` on
       `fj_show_member_benefits` wrapped in an `unless customer` — read in
       theme-full, not assumed. So it exists only for a logged-out visitor on a
       product that shows member benefits, and it can legitimately be missing.
       When it is missing the theme handler's own `if (lm)` fails, nothing
       cancels the click, and the anchor navigates with the `return_url` it has
       always carried. Every one of these controls is still a real
       `<a href="/account/login?return_url=…">`; that has not changed and is
       what makes the fallback free.

       WHAT WE STILL DO ON THE WAY THROUGH (primeThemeLogin, below): lift the
       modal out of the buy box in case the click beats the `load` listener,
       point the form's `return_to` at this page, and take focus into the
       dialog — three things the theme handler does not do. */
    wireSignIn() {
      this.claimSignInLinks(this)

      // Focus has to come back to the control that opened the popup. The theme
      // owns the close paths (a [data-fj-login-close] click and Escape), so we
      // watch for them rather than re-implementing them.
      this.restoreLoginFocus = this.restoreLoginFocus.bind(this)
      document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return
        if (!event.target.closest('[data-fj-login-close]')) return
        window.setTimeout(this.restoreLoginFocus, 0)
      })
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') window.setTimeout(this.restoreLoginFocus, 0)
      })
    }

    claimSignInLinks(root) {
      if (!root) return
      root.querySelectorAll('a[href*="/account/login"]').forEach((link) => {
        if (link.dataset.nosSigninWired === 'true') return
        link.dataset.nosSigninWired = 'true'
        // No preventDefault and no stopPropagation: the click is meant to reach
        // the theme's document-level handler. This only prepares the ground.
        link.addEventListener('click', () => this.primeThemeLogin())
      })
    }

    /* Returns true when the theme's popup is available and has been prepared.
       False means "this click is going to navigate", which is the correct
       outcome, not an error. */
    primeThemeLogin() {
      // Lift it out of the buy box NOW: a fast click on the hero line can beat
      // the `load` listener that normally does this.
      this.rescueLoginModal()
      const modal = document.getElementById('fj-login-modal')
      if (!modal) return false

      /* The theme renders the login form with `return_to: request.path`, so a
         successful sign-in already comes back to this page. The query string is
         added here so a member arriving from an email campaign returns as the
         same visitor. The FRAGMENT is deliberately NOT appended: Shopify
         validates `return_to`, and a value it rejects strands the shopper on
         /account rather than failing loudly. The cost is that signing in from
         the crypt returns to the top of the page instead of to
         #nosferatu-membership — recorded in the build note. */
      const field = modal.querySelector('input[name="return_to"]')
      if (field) field.value = window.location.pathname + window.location.search

      /* Revision 5, item 1. A submitted login form is the visitor genuinely
         walking through the "I am of the Circle" door, so it promotes a
         provisional gate answer to a real one. Bound once, on the modal, in
         the capture phase so a handler that cancels the submit cannot hide it
         from us. Harmless when the gate was never involved. */
      if (modal.dataset.nosGateCommit !== 'true') {
        modal.dataset.nosGateCommit = 'true'
        modal.addEventListener('submit', () => this.commitGateAnswer(), true)
      }

      this.loginReturnFocus = document.activeElement
      window.setTimeout(() => {
        if (modal.hidden) return
        const first = modal.querySelector('input:not([type="hidden"]), button[type="submit"]')
        first?.focus()
      }, 40)
      return true
    }

    restoreLoginFocus() {
      if (!this.loginReturnFocus) return
      const modal = document.getElementById('fj-login-modal')
      if (modal && !modal.hidden) return
      if (this.loginReturnFocus instanceof HTMLElement) this.loginReturnFocus.focus()
      this.loginReturnFocus = null
    }

    /* ------------------------------------------------------------------ gate */
    wireGate() {
      this.acceptButton?.addEventListener('click', () => this.acceptInvitation())
      this.querySelector('[data-nos-gate-scrim]')?.addEventListener('click', () => this.closeGate())
      this.querySelector('[data-nos-gate-close]')?.addEventListener('click', () => this.closeGate())
      this.querySelector('[data-nos-gate-new]')?.addEventListener('click', () => {
        // Which door the visitor came in by decides where "I am new to the
        // night" lets them out. Read BEFORE closeGate(), which resets nothing
        // but is the natural place for a future reset to creep in.
        const intent = this.gateIntent
        this.markGateAnswered()
        this.closeGate()
        if (intent === 'crypt') this.revealCrypt()
        else this.descendToChapterOne()
      })

      /* ------------------------------------------------------------------
         REVISION 2, ITEM 13 — why "I am of the Circle — sign in" did nothing.

         The control was always a real <a href="/account/login?return_url=…">.
         What killed it is a DOCUMENT-level click handler the theme installs
         from `snippets/product-info.liquid`:

             var nativeLogin = e.target.closest('a[href^="/account/login"]')
             if (nativeLogin) { var lm = document.getElementById('fj-login-modal')
               if (lm) { e.preventDefault(); lm.hidden = false; return } }

         It swallows EVERY link to the login route on the page and opens the
         theme's own `#fj-login-modal` instead. On this template that modal is
         emitted inside the featured-product section — which this component
         relocates into the crypt card, where `.nos-card__back { display:none }`
         keeps it (and everything in it) invisible until the crypt is open. So
         the click was intercepted, the default was cancelled, and the thing it
         opened could not be seen. Hence "does nothing".

         Stopping propagation on the anchor keeps the event off `document`, so
         the native navigation happens. Nothing in a theme or app file changed.
         (rescueLoginModal() below fixes the same handler's other victim: the
         header account icon.)
         ------------------------------------------------------------------ */
      this.gateMemberLink = this.querySelector('[data-nos-gate-member]')
      this.gateMemberLink?.addEventListener('click', () => {
        // Revision 4, item 8: the stopPropagation() that used to be here is
        // gone on purpose — see claimSignInLinks(). The click now reaches the
        // theme's handler and opens #fj-login-modal in place; when that modal
        // is not on the page the anchor navigates with its return_url exactly
        // as it did in round 3. The gate is closed first so the popup does not
        // open behind our own dialog.
        //
        // REVISION 5, ITEM 1 — and this is where the reported bug lived.
        // Round 3 could record the answer unconditionally because this door
        // NAVIGATED: the visitor left the page. Round 4 turned it into the
        // theme's in-place lightbox, and a lightbox can be dismissed. The
        // combination stranded the visitor: gate marked answered, still an
        // anonymous prospect, and neither "Accept the invitation" nor "Open the
        // crypt" would ever raise the gate again for the rest of the session.
        //
        // So the answer is PROVISIONAL while the popup is the thing that
        // opened. It becomes real when the login form is actually submitted
        // (commitGateAnswer(), wired in primeThemeLogin) and evaporates if the
        // popup is closed without one (gateIsAnswered()). When there is no
        // popup the click navigates, exactly as in round 3, and the answer is
        // recorded outright.
        // claimSignInLinks() wired primeThemeLogin() on this same anchor first
        // (wireSignIn runs before wireGate), so the modal has already been
        // lifted out of the buy box by the time this runs.
        const modal = document.getElementById('fj-login-modal')
        this.markGateAnswered({ pending: Boolean(modal) })
        this.closeGate()
      })

      this.onGateKeydown = this.onGateKeydown.bind(this)
    }

    /* `pending: true` records a door the visitor has only half walked through —
       the sign-in popup is open and they may yet close it. Stored as a distinct
       value so a reload cannot promote it to a real answer. */
    markGateAnswered(options) {
      const pending = Boolean(options && options.pending)
      this.gateAnswered = true
      this.gatePendingSignIn = pending
      try {
        window.sessionStorage.setItem(this.gateStorageKey, pending ? 'pending' : 'true')
      } catch (error) {
        // The gate still works without storage; it just asks again on reload.
      }
    }

    /* The visitor actually submitted the theme's login form. Round 3's rule
       stands: someone who says "I am of the Circle", signs in, and comes back
       still unauthenticated is not asked the same question twice. */
    commitGateAnswer() {
      if (!this.gateAnswered) return
      this.markGateAnswered()
    }

    clearGateAnswer() {
      this.gateAnswered = false
      this.gatePendingSignIn = false
      try {
        window.sessionStorage.removeItem(this.gateStorageKey)
      } catch (error) {
        // Nothing to clean up if storage is unavailable.
      }
    }

    /* The single question both doors onto the gate ask. Dismissal — Esc, the
       scrim, the × — was never an answer and still is not; this adds the fourth
       way out that round 4 created, an abandoned sign-in popup. Resolving it
       here rather than on the popup's close events means no close path can be
       missed: whatever happened to that popup, the state is re-read at the
       moment the gate is asked for. */
    gateIsAnswered() {
      if (!this.gateAnswered) return false
      if (!this.gatePendingSignIn) return true
      const modal = document.getElementById('fj-login-modal')
      if (modal && !modal.hidden) return true
      this.clearGateAnswer()
      return false
    }

    /* The theme's global "Member Log In" popup is rendered inside the
       featured-product section, so on this template it (a) inherits the
       story's cream-on-dark palette onto a white panel and (b) can end up
       inside the collapsed crypt card, where it cannot be seen at all. Lifting
       it to <body> and tagging it lets the campaign-scoped CSS repaint it and
       guarantees it opens wherever it is triggered from — including the theme
       header's account icon, which goes through the same handler.
       This moves a node the theme created; it edits no theme file. */
    rescueLoginModal() {
      if (window.Shopify?.designMode) return
      const modal = document.getElementById('fj-login-modal')
      if (!modal || modal.dataset.nosRescued === 'true') return
      modal.dataset.nosRescued = 'true'
      modal.classList.add('nos-login-rescued')
      if (modal.parentNode !== document.body) document.body.appendChild(modal)
    }

    acceptInvitation() {
      // Signed-in members and member-email arrivals are already identified —
      // they are never asked who goes there. Neither is anyone who already
      // answered the gate this session (revision 3, item B).
      if (this.memberView || this.gateIsAnswered()) {
        this.descendToChapterOne()
        return
      }
      this.openGate('chapter')
    }

    openGate(intent) {
      this.gateIntent = intent === 'crypt' ? 'crypt' : 'chapter'

      // One anchor, two return URLs. Answering at the crypt has to come back to
      // the crypt; answering in the hero comes back to Chapter I. Both are
      // rendered by Liquid with url_encode, so the fragment survives.
      if (this.gateMemberLink) {
        const href = this.gateIntent === 'crypt'
          ? this.gateMemberLink.dataset.nosHrefCrypt
          : this.gateMemberLink.dataset.nosHrefChapter
        if (href) this.gateMemberLink.setAttribute('href', href)
      }

      if (!this.gate) {
        // No dialog to show — do the thing the visitor asked for rather than
        // stranding them.
        if (this.gateIntent === 'crypt') this.revealCrypt()
        else this.descendToChapterOne()
        return
      }
      this.gateReturnFocus = document.activeElement
      // The story element clips and isolates its own stacking context, so the
      // dialog is portalled to <body> to guarantee it sits above the theme
      // header and is never clipped. Its CSS re-declares the palette tokens.
      if (this.gate.parentNode !== document.body) document.body.appendChild(this.gate)
      this.gate.hidden = false
      document.documentElement.style.overflow = 'hidden'
      this.gate.addEventListener('keydown', this.onGateKeydown)
      const first = this.gateFocusable()[0]
      window.setTimeout(() => first?.focus(), 30)
      this.playDoorCreak()
      this.announce(this.dataset.gateOpenLabel)
    }

    closeGate() {
      if (!this.gate || this.gate.hidden) return
      this.gate.hidden = true
      document.documentElement.style.overflow = ''
      this.gate.removeEventListener('keydown', this.onGateKeydown)
      if (this.gateReturnFocus instanceof HTMLElement) this.gateReturnFocus.focus()
    }

    gateFocusable() {
      if (!this.gatePanel) return []
      return Array.from(this.gatePanel.querySelectorAll('a[href], button:not([disabled])'))
        .filter((node) => node.offsetParent !== null)
    }

    onGateKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeGate()
        return
      }
      if (event.key !== 'Tab') return
      const focusable = this.gateFocusable()
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    descendToChapterOne() {
      if (!this.chapterOne) return
      this.chapterOne.scrollIntoView({
        behavior: this.prefersReducedMotion || !this.motionAllowed ? 'auto' : 'smooth',
        block: 'start'
      })
      window.setTimeout(() => this.letterButton?.focus({ preventScroll: true }), 600)
    }

    /* ---------------------------------------------------------------- letter
       Revision 3, item E. The message is written INSIDE the letter now
       ([data-nos-letter-ink], aria-hidden inside the button so the button keeps
       a stable accessible name), and announced separately through the .nos-sr
       live region so a screen reader still hears every word.

       The first tap breaks the seal AND shows message one; every tap after that
       swaps the ink in place. Under reduced motion or a stilled night the swap
       is instant — the CSS transitions are already neutralised there, and the
       timeout collapses to 0 so the two never disagree. */
    wireLetter() {
      this.letterButton?.addEventListener('click', () => this.readLetter())
    }

    readLetter() {
      if (!this.letterMessages.length) return
      const instant = this.prefersReducedMotion || !this.motionAllowed
      const next = this.letterMessages[this.letterIndex % this.letterMessages.length]

      if (!this.letterOpened) {
        this.letterOpened = true
        this.letterButton?.classList.add('is-open')
        // "Break the seal" is no longer true once it is broken.
        const openLabel = this.dataset.letterOpenLabel
        if (openLabel) {
          this.letterButton?.setAttribute('aria-label', openLabel)
          if (this.letterPrompt) this.letterPrompt.textContent = openLabel
        }
      }

      this.letterInk?.classList.add('is-changing')
      window.setTimeout(() => {
        if (this.letterInk) this.letterInk.textContent = next
        this.letterInk?.classList.remove('is-changing')
        // The live region is written after the ink so the announcement matches
        // what is on screen rather than racing it.
        if (this.letterResult) this.letterResult.textContent = next
        this.letterIndex += 1
      }, instant ? 0 : 180)
    }

    /* ---------------------------------------------------------------- scents */
    /* Hover blooms are CSS. Tap/click and keyboard toggle the same state so the
       vignettes work identically on touch devices. */
    wireScents() {
      this.querySelectorAll('[data-nos-scent]').forEach((scent) => {
        const trigger = scent.querySelector('.nos-scent__trigger')
        trigger?.addEventListener('click', () => {
          const open = scent.classList.toggle('is-open')
          trigger.setAttribute('aria-expanded', open ? 'true' : 'false')
        })
      })
    }

    /* ------------------------------------------------- III — doorway plate */
    /* Revision 2, item 11. Hover and keyboard focus are handled entirely in
       CSS; this only adds the touch path, where there is no hover to give.
       A tap toggles the reveal and `aria-pressed` reports it. Nothing here is
       required for the still or its alt text to be present. */
    wirePlate() {
      this.plate = this.querySelector('[data-nos-plate]')
      this.plate?.addEventListener('click', () => {
        const revealed = this.plate.classList.toggle('is-revealed')
        this.plate.setAttribute('aria-pressed', revealed ? 'true' : 'false')
      })
    }

    /* --------------------------------------------------------- blood trail */
    /* Revision 2, item 10 -> revision 4, item 7b -> REVISION 7, ITEM 2.

       Desktop pointers only, never under reduced motion, never on touch. THREE
       node types now, all pooled and created once:

         MOVING  -> a short SMEAR laid down along the direction of travel and
                    lengthened by speed, which then sags and dries; and, every
                    few smears, a DROPLET that falls from the blade. Round 4
                    deleted the droplets outright and the client read the
                    result as flatter than round 2. They are back beside the
                    smears rather than instead of them.
         RESTING -> after ~520ms of stillness, or for as long as the pointer is
                    held down, a single PUDDLE grows beneath the point in steps,
                    caps at the size the CSS declares, holds, then dries slowly.

       WHAT MADE IT LAG, and what was done about it — three separate faults:

       1. EVERY SPAWN FORCED A SYNCHRONOUS LAYOUT. `void smear.offsetWidth`,
          used to restart the dry-down on a reused node, cost ~120us of the
          ~150us each spawn took — and it ran INSIDE the pointermove handler,
          which is the one place a synchronous layout is guaranteed to be felt.
          The restart is now done by alternating between two identically-bodied
          keyframes (`nos-smear-dry-a` / `-b`): a different animation-name is a
          restart by definition, with no reflow read at all.

       2. THE WORK RAN PER EVENT. A fast mouse delivers events faster than the
          compositor draws them, so some of that work was for frames that never
          existed. `pointermove` now only records x/y/t into fields and raises
          a flag; ALL DOM work happens in one requestAnimationFrame per frame,
          which is also where the rest-timer is armed (it used to be a
          clearTimeout + setTimeout on literally every move).

       3. THE MARK ARRIVED LATE. Round 4 wanted 96px of travel AND 130ms since
          the last mark before laying one down, then took 14% of a 1.2-2.1s
          animation to reach peak opacity. Worst case the smear appeared some
          400ms behind the pointer, which is what "lags slightly" describes.
          Now: 30px and 42ms, peaking at 8% of a 0.78-1.08s animation, so the
          mark is under the pointer's tail rather than trailing the gesture.

       The layer is fixed, `pointer-events: none` at every level, `contain:
       layout paint`, and portalled to <body> — it cannot intercept a click or
       shift layout. It stands down over exactly the same five places as
       before: the purchase form, the login popup, both dialogs, form fields. */
    wireDrips() {
      this.dripLayer = this.querySelector('[data-nos-drips]')
      if (!this.dripLayer) return
      const finePointer = window.matchMedia('(pointer: fine)').matches &&
        window.matchMedia('(hover: hover)').matches
      // The system preference is not a preference here — it is a hard stop.
      if (!finePointer || this.prefersReducedMotion) return

      // Portalled to <body> for the same reason the gate and lightbox are:
      // the story clips its own x axis and isolates a stacking context.
      if (this.dripLayer.parentNode !== document.body) document.body.appendChild(this.dripLayer)

      const build = (className, count) => {
        const pool = []
        for (let index = 0; index < count; index += 1) {
          const node = document.createElement('i')
          node.className = className
          this.dripLayer.appendChild(node)
          pool.push(node)
        }
        return pool
      }
      this.smearPool = build('nos-smear', NOS_SMEAR_POOL)
      this.dropPool = build('nos-drop', NOS_DROP_POOL)
      // One puddle for the life of the page. There is never a second.
      this.puddle = document.createElement('i')
      this.puddle.className = 'nos-puddle'
      this.dripLayer.appendChild(this.puddle)

      this.dripLayer.hidden = false
      this.dripLayer.classList.toggle('is-stilled', !this.motionAllowed)
      this.smearIndex = 0
      this.dropIndex = 0
      this.sinceDrop = 0
      this.dripLastX = null
      this.dripLastY = null
      this.dripLastTime = 0
      this.dripDistance = 0
      this.dripLastSpawn = 0
      this.dripHeld = false
      this.puddleActive = false
      this.puddleGrow = 0

      // The one frame-scheduled batch. `dripPending` holds the newest sample
      // seen since the last frame; the flag is what stops us queueing a second
      // callback for the same frame.
      this.dripFrame = 0
      this.dripPending = null
      this.dripRestArmed = false
      this.flushDrips = this.flushDrips.bind(this)

      this.onDripMove = this.onDripMove.bind(this)
      this.onDripDown = this.onDripDown.bind(this)
      this.onDripUp = this.onDripUp.bind(this)
      window.addEventListener('pointermove', this.onDripMove, { passive: true })
      window.addEventListener('pointerdown', this.onDripDown, { passive: true })
      window.addEventListener('pointerup', this.onDripUp, { passive: true })
      window.addEventListener('pointercancel', this.onDripUp, { passive: true })
    }

    /* The one list of places the trail must not appear. The purchase form and
       the login popup are usability; the two dialogs are because a smear over a
       modal reads as a rendering fault, not as atmosphere. */
    dripBlocked(target) {
      if (!(target instanceof Element)) return false
      return Boolean(target.closest('.nos-buybox-slot, .fj-login-modal, .nos-gate, .nos-lightbox, input, select, textarea'))
    }

    /* THE LISTENER DOES NO DOM WORK. It records the newest sample and asks for
       a frame; that is the whole of it, and it is why the per-event cost is now
       a rounding error. Accumulating `dripDistance` here rather than in the
       frame matters: a fast mouse can deliver several moves between frames and
       the travel from all of them should count. */
    onDripMove(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return
      const x = event.clientX
      const y = event.clientY
      const now = event.timeStamp || Date.now()
      if (this.dripLastX === null) {
        this.dripLastX = x
        this.dripLastY = y
        this.dripLastTime = now
        this.dripRestArmed = false
        this.dripPending = { x, y, dx: 0, dy: 0, now, target: event.target, moved: 0 }
        this.requestDripFrame()
        return
      }
      const dx = x - this.dripLastX
      const dy = y - this.dripLastY
      const step = Math.sqrt((dx * dx) + (dy * dy))
      this.dripLastX = x
      this.dripLastY = y
      this.dripLastTime = now
      this.dripDistance += step

      const pending = this.dripPending
      if (pending) {
        // fold this sample into the one already waiting for the frame
        pending.x = x
        pending.y = y
        pending.dx += dx
        pending.dy += dy
        pending.moved += step
        pending.elapsed = Math.max(1, now - pending.now)
        pending.now = now
        pending.target = event.target
      } else {
        this.dripPending = { x, y, dx, dy, now, elapsed: 1, target: event.target, moved: step }
      }
      this.requestDripFrame()
    }

    requestDripFrame() {
      if (this.dripFrame) return
      this.dripFrame = window.requestAnimationFrame(this.flushDrips)
    }

    /* ONE frame, ONE batch: every style write and every class change the trail
       makes happens here and nowhere else. No layout is read, so nothing in
       this function can force a synchronous reflow. */
    flushDrips() {
      this.dripFrame = 0
      const sample = this.dripPending
      this.dripPending = null
      if (!sample) return

      const { x, y, now } = sample
      const stilled = !this.motionAllowed

      // Real movement ends a rest. A press-and-hold does NOT: dragging with the
      // button down keeps the puddle it started, which is the whole point of
      // the hold gesture.
      if (!this.dripHeld) {
        if (sample.moved > 3 && this.puddleActive) this.endPuddle()
        // Re-arming used to be a clearTimeout + setTimeout on EVERY move. Once
        // per frame is enough, and the timer only restarts when the pointer
        // actually travelled.
        if (sample.moved > 1 || !this.dripRestArmed) this.armPuddle(x, y)
      }

      if (this.dripDistance < (stilled ? 96 : 30)) return
      if (now - this.dripLastSpawn < (stilled ? 190 : 42)) return
      this.dripDistance = 0
      if (this.dripBlocked(sample.target)) return
      this.dripLastSpawn = now

      const speed = sample.moved / (sample.elapsed || 16)
      this.spawnSmear(x, y, sample.dx, sample.dy, speed)

      // A droplet every few smears, and sooner when the blade is moving fast —
      // blood is thrown off an edge, it does not shed evenly. Never more than
      // one per frame, so the ceiling is the frame rate.
      this.sinceDrop += 1
      if (!stilled && this.sinceDrop >= (speed > 1.1 ? 3 : 5)) {
        this.sinceDrop = 0
        this.spawnDrop(x, y, speed)
      }
    }

    onDripDown(event) {
      if (event.pointerType && event.pointerType !== 'mouse') return
      if (this.dripBlocked(event.target)) return
      this.dripHeld = true
      window.clearTimeout(this.puddleTimer)
      this.dripRestArmed = false
      // A press throws one droplet immediately, so the gesture answers on the
      // frame it happens rather than waiting out the 520ms rest threshold.
      if (this.motionAllowed) this.spawnDrop(event.clientX, event.clientY, 0.4)
      this.beginPuddle(event.clientX, event.clientY)
    }

    onDripUp() {
      if (!this.dripHeld) return
      this.dripHeld = false
      this.endPuddle()
    }

    armPuddle(x, y) {
      window.clearTimeout(this.puddleTimer)
      this.dripRestArmed = true
      this.puddleTimer = window.setTimeout(
        () => this.beginPuddle(x, y),
        this.motionAllowed ? 520 : 900
      )
    }

    beginPuddle(x, y) {
      if (!this.puddle || this.puddleActive) return
      // elementFromPoint is safe here: the trail layer is pointer-events: none,
      // so it can never be the answer.
      if (this.dripBlocked(document.elementFromPoint(x, y))) return
      this.puddleActive = true
      this.puddleGrow = 0.22
      this.puddle.style.setProperty('--nos-puddle-x', `${Math.round(x - 22)}px`)
      this.puddle.style.setProperty('--nos-puddle-y', `${Math.round(y - 4)}px`)
      this.puddle.style.setProperty('--nos-puddle-grow', String(this.puddleGrow))
      this.puddle.classList.remove('is-drying')
      this.puddle.classList.add('is-pooling')
      window.clearInterval(this.puddleInterval)
      this.puddleInterval = window.setInterval(() => {
        // The cap is 1: the size in the CSS is the largest this can ever be.
        this.puddleGrow = Math.min(1, this.puddleGrow + 0.13)
        this.puddle.style.setProperty('--nos-puddle-grow', String(this.puddleGrow))
        if (this.puddleGrow < 1) return
        window.clearInterval(this.puddleInterval)
        // It has stopped spreading. Let it stand a moment, then dry.
        this.puddleDryTimer = window.setTimeout(() => this.endPuddle(), 1400)
      }, 420)
    }

    endPuddle() {
      window.clearInterval(this.puddleInterval)
      window.clearTimeout(this.puddleDryTimer)
      if (!this.puddle || !this.puddleActive) return
      this.puddleActive = false
      this.puddle.classList.remove('is-pooling')
      this.puddle.classList.add('is-drying')
      window.clearTimeout(this.puddleResetTimer)
      this.puddleResetTimer = window.setTimeout(() => {
        if (this.puddleActive) return
        this.puddle.classList.remove('is-drying')
        this.puddleGrow = 0
        this.puddle.style.setProperty('--nos-puddle-grow', '0')
      }, 2700)
    }

    /* Restart a pooled node's animation WITHOUT reading layout.

       The old way was `classList.remove(...)`, `void node.offsetWidth`,
       `classList.add(...)` — and that middle line was ~120us of synchronous
       layout per spawn, inside an input handler. Two keyframes with identical
       bodies and different names do the same job for nothing: swapping which
       one is applied is a change of `animation-name`, and a change of
       animation-name IS a restart. No reflow, no read, no flush. */
    restartTrailNode(node, base) {
      const onA = node.classList.contains(`${base}-a`)
      node.classList.remove(`${base}-${onA ? 'a' : 'b'}`)
      node.classList.add(`${base}-${onA ? 'b' : 'a'}`)
    }

    spawnSmear(x, y, dx, dy, speed) {
      if (!this.smearPool || !this.smearPool.length) return
      const smear = this.smearPool[this.smearIndex % this.smearPool.length]
      this.smearIndex += 1
      // +180: the smear trails BEHIND the point, the way a wiped edge would.
      const angle = (Math.atan2(dy, dx) * (180 / Math.PI)) + 180
      const length = Math.max(9, Math.min(34, 9 + (speed * 13)))
      const thickness = 3 + (Math.random() * 2.2)
      const style = smear.style
      style.setProperty('--nos-smear-x', `${Math.round(x)}px`)
      style.setProperty('--nos-smear-y', `${Math.round(y - (thickness / 2))}px`)
      style.setProperty('--nos-smear-a', `${angle.toFixed(1)}deg`)
      style.setProperty('--nos-smear-len', `${length.toFixed(1)}px`)
      style.setProperty('--nos-smear-thick', `${thickness.toFixed(1)}px`)
      style.setProperty('--nos-smear-sag', `${Math.round(4 + (Math.random() * 7))}px`)
      // Round 4 dried over 1.2-2.1s, which at the new spawn rate would have the
      // pool recycling a node that was still visibly wet. Shorter, and the
      // marks still overlap: 18 nodes at ~24/s is a 0.75s round trip.
      style.setProperty('--nos-smear-time', `${Math.round(780 + (Math.random() * 300))}ms`)
      this.restartTrailNode(smear, 'is-wet')
    }

    /* REVISION 7, ITEM 2 — the droplets round 4 deleted, back beside the
       smears. One node, one fall: it drops from the blade, stretches as it
       goes and dries out. Pure transform + opacity, same pooled budget rule. */
    spawnDrop(x, y, speed) {
      if (!this.dropPool || !this.dropPool.length) return
      const drop = this.dropPool[this.dropIndex % this.dropPool.length]
      this.dropIndex += 1
      const style = drop.style
      style.setProperty('--nos-drop-x', `${Math.round(x + (Math.random() * 9) - 4)}px`)
      style.setProperty('--nos-drop-y', `${Math.round(y + 2)}px`)
      style.setProperty('--nos-drop-size', `${(2.6 + (Math.random() * 2)).toFixed(1)}px`)
      // faster blade, longer throw
      style.setProperty('--nos-drop-fall', `${Math.round(26 + (speed * 16) + (Math.random() * 26))}px`)
      style.setProperty('--nos-drop-time', `${Math.round(620 + (Math.random() * 420))}ms`)
      this.restartTrailNode(drop, 'is-falling')
    }

    /* -------------------------------------------------------------- material */
    wireMaterialToggle() {
      const buttons = Array.from(this.querySelectorAll('[data-nos-material]'))
      const images = Array.from(this.querySelectorAll('[data-nos-material-image]'))
      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const material = button.dataset.nosMaterial
          buttons.forEach((other) => {
            const active = other === button
            other.classList.toggle('is-active', active)
            other.setAttribute('aria-pressed', active ? 'true' : 'false')
          })
          images.forEach((image) => {
            const active = image.dataset.nosMaterialImage === material
            image.classList.toggle('is-active', active)
            image.setAttribute('aria-hidden', active ? 'false' : 'true')
          })
          if (this.materialScope) this.materialScope.dataset.material = material
          this.vault?.classList.toggle('is-silver', material === 'silver')
        })
      })
    }

    /* ------------------------------------------------------------ coffin keys */
    wireKeys() {
      this.querySelectorAll('[data-nos-key]').forEach((button) => {
        if (this.foundKeys.has(button.dataset.nosKey)) button.disabled = true
        button.addEventListener('click', () => this.collectKey(button))
      })
      this.syncKeys(false)
    }

    collectKey(button) {
      if (button.disabled) return
      button.disabled = true
      this.foundKeys.add(button.dataset.nosKey)
      try {
        window.sessionStorage.setItem(this.keyStorageKey, JSON.stringify(Array.from(this.foundKeys)))
      } catch (error) {
        // The hunt still works without storage; it just will not survive a reload.
      }
      this.syncKeys(true)
    }

    syncKeys(announce) {
      const found = this.foundKeys.size
      this.keyCounts.forEach((node) => { node.textContent = String(found) })
      this.foundKeys.forEach((id) => this.classList.add(`has-key-${id}`))
      this.classList.toggle('has-keys', found > 0)
      this.tally?.setAttribute('aria-hidden', found > 0 ? 'false' : 'true')
      const complete = found >= 3
      this.classList.toggle('is-keys-complete', complete)
      // Progressive unveiling. On the third key the remaining shrouds dissolve
      // in sequence rather than all at once.
      this.syncVeils(announce, complete)
      if (announce && complete) this.celebrate()
      if (!announce) return
      this.announce(complete
        ? this.dataset.keysCompleteLabel
        : `${found} ${this.dataset.keyFoundLabel}`)
    }

    /* ----------------------------------------------------------------- veils */
    /* PRESENTATION ONLY. A veil covers a photograph — never a price, a CTA, a
       buy box, a caption or alt text, and it never disables a control. The
       markup ships every veil `hidden`; this method is the only thing that
       reveals one, so a JS failure leaves all imagery visible.
       Steps are keyed on the NUMBER of keys held (1 → hero reliquary,
       2 → Chapter IV bath scene + specimen plate, 3 → the ring vault), which is
       order-independent and therefore forgiving of an out-of-order hunt. */
    applyVeils() {
      this.veils = []
      // An authenticated member is not hunting: everything is open from the
      // start. The hunt is for prospects and UTM arrivals.
      if (this.memberAuthorized && this.memberView) {
        this.syncReliquaryLife()
        return
      }
      Array.from(this.querySelectorAll('[data-nos-veil]')).forEach((host) => {
        const layer = host.querySelector('[data-nos-veil-layer]')
        if (!layer) return
        const needed = Number(host.dataset.nosVeil) || 1
        const earned = this.foundKeys.size >= needed
        if (!earned) layer.hidden = false
        this.veils.push({ host, layer, needed, veiled: !earned })
      })
      this.syncReliquaryLife()
    }

    /* Revision 4, item 4. The hero circle only comes to life once it has been
       unveiled — a shrouded photograph flickering under its own shroud would be
       nonsense. `is-alive` is the single hook; which of the three motion states
       is live is decided entirely in CSS, so this method never has to know. */
    syncReliquaryLife() {
      this.reliquaryDisc?.classList.toggle('is-alive', !this.isVeiled(1))
    }

    syncVeils(animate, stagger) {
      if (!this.veils || !this.veils.length) return
      let queued = 0
      this.veils.forEach((record) => {
        if (!record.veiled || this.foundKeys.size < record.needed) return
        const delay = animate && stagger ? queued * 260 : 0
        queued += 1
        if (!delay) {
          this.unveil(record, animate)
          return
        }
        window.setTimeout(() => this.unveil(record, animate), delay)
      })
    }

    unveil(record, animate) {
      if (!record || !record.veiled) return
      record.veiled = false
      this.syncReliquaryLife()
      const instant = !animate || this.prefersReducedMotion || !this.motionAllowed
      if (instant) {
        record.layer.hidden = true
        return
      }
      record.layer.classList.add('is-lifting')
      window.setTimeout(() => {
        record.layer.hidden = true
        record.layer.classList.remove('is-lifting')
      }, 760)
    }

    isVeiled(step) {
      return Boolean(this.veils?.some((record) => record.veiled && record.needed === step))
    }

    /* A veiled thing opens nothing — it says why instead. Returns true when it
       handled the interaction, so the caller knows to stop. */
    hintVeil(step) {
      const record = this.veils?.find((item) => item.veiled && item.needed === step)
      if (!record) return false
      record.layer.classList.remove('is-hinting')
      // force a reflow so the pulse restarts on a repeated tap
      void record.layer.offsetWidth
      record.layer.classList.add('is-hinting')
      window.setTimeout(() => record.layer.classList.remove('is-hinting'), 900)
      this.announce(this.dataset.veilLockedLabel)
      return true
    }

    /* ----------------------------------------------------- third-key flourish */
    /* Self-contained and skippable: pointer-events: none throughout, so it can
       never sit between a shopper and a control, it ends itself after 2.6s, and
       any tap or key press ends it sooner. Reduced motion / stilled night skip
       it entirely — the aria-live announcement and the Chapter II completion
       line carry the same news. */
    celebrate() {
      if (this.prefersReducedMotion || !this.motionAllowed || !this.celebration) {
        this.classList.add('is-celebrated')
        return
      }
      this.classList.add('is-celebrating')
      this.celebration.hidden = false
      window.clearTimeout(this.celebrationTimer)
      this.celebrationTimer = window.setTimeout(this.endCelebration, 2600)
      document.addEventListener('pointerdown', this.endCelebration, { once: true })
      document.addEventListener('keydown', this.endCelebration, { once: true })
    }

    endCelebration() {
      window.clearTimeout(this.celebrationTimer)
      document.removeEventListener('pointerdown', this.endCelebration)
      document.removeEventListener('keydown', this.endCelebration)
      if (!this.celebration || this.celebration.hidden) return
      this.celebration.hidden = true
      this.classList.remove('is-celebrating')
      this.classList.add('is-celebrated')
    }

    /* ------------------------------------------------- ring photo lightbox */
    wireVault() {
      this.lightbox = this.querySelector('[data-nos-lightbox]')
      this.lightboxPanel = this.querySelector('[data-nos-lightbox-panel]')
      this.lightboxImage = this.querySelector('[data-nos-lightbox-image]')
      this.lightboxCaption = this.querySelector('[data-nos-lightbox-caption]')
      this.lightboxCount = this.querySelector('[data-nos-lightbox-count]')
      this.lightboxSlides = Array.from(this.querySelectorAll('[data-nos-photo]'))
        .map((node) => ({
          src: (node.dataset.src || '').trim(),
          alt: node.dataset.alt || '',
          caption: node.dataset.caption || ''
        }))
        .filter((slide) => slide.src)
      this.lightboxIndex = 0
      this.onLightboxKeydown = this.onLightboxKeydown.bind(this)

      const medallions = Array.from(this.querySelectorAll('[data-nos-medallion]'))
      medallions.forEach((button, index) => {
        button.addEventListener('click', () => {
          // A veiled medallion opens nothing; it hints its own veil instead.
          if (this.hintVeil(3)) return
          if (!this.lightboxSlides.length) return
          this.openLightbox(index % this.lightboxSlides.length, button)
        })
      })

      this.querySelector('[data-nos-lightbox-scrim]')?.addEventListener('click', () => this.closeLightbox())
      this.querySelector('[data-nos-lightbox-close]')?.addEventListener('click', () => this.closeLightbox())
      this.querySelector('[data-nos-lightbox-prev]')?.addEventListener('click', () => this.stepLightbox(-1))
      this.querySelector('[data-nos-lightbox-next]')?.addEventListener('click', () => this.stepLightbox(1))

      const figure = this.querySelector('[data-nos-lightbox-figure]')
      if (figure) {
        let startX = 0
        let startY = 0
        figure.addEventListener('touchstart', (event) => {
          startX = event.changedTouches[0].clientX
          startY = event.changedTouches[0].clientY
        }, { passive: true })
        figure.addEventListener('touchend', (event) => {
          const dx = event.changedTouches[0].clientX - startX
          const dy = event.changedTouches[0].clientY - startY
          if (Math.abs(dx) < 42 || Math.abs(dx) < Math.abs(dy)) return
          this.stepLightbox(dx < 0 ? 1 : -1)
        }, { passive: true })
      }
    }

    openLightbox(index, trigger) {
      if (!this.lightbox || !this.lightboxSlides.length) return
      this.lightboxReturnFocus = trigger || document.activeElement
      // Same reason as the gate: the story clips and isolates its own stacking
      // context, so the dialog is portalled to <body>.
      if (this.lightbox.parentNode !== document.body) document.body.appendChild(this.lightbox)
      this.showSlide(index)
      this.lightbox.hidden = false
      document.documentElement.style.overflow = 'hidden'
      this.lightbox.addEventListener('keydown', this.onLightboxKeydown)
      window.setTimeout(() => this.lightboxFocusable()[0]?.focus(), 30)
    }

    closeLightbox() {
      if (!this.lightbox || this.lightbox.hidden) return
      this.lightbox.hidden = true
      document.documentElement.style.overflow = ''
      this.lightbox.removeEventListener('keydown', this.onLightboxKeydown)
      if (this.lightboxReturnFocus instanceof HTMLElement) this.lightboxReturnFocus.focus()
    }

    stepLightbox(delta) {
      const total = this.lightboxSlides.length
      if (!total) return
      this.showSlide((this.lightboxIndex + delta + total) % total)
    }

    showSlide(index) {
      const slide = this.lightboxSlides[index]
      if (!slide) return
      this.lightboxIndex = index
      if (this.lightboxImage) {
        this.lightboxImage.src = slide.src
        this.lightboxImage.alt = slide.alt
      }
      if (this.lightboxCaption) this.lightboxCaption.textContent = slide.caption
      if (this.lightboxCount) {
        this.lightboxCount.textContent = `${index + 1} / ${this.lightboxSlides.length}`
      }
    }

    lightboxFocusable() {
      if (!this.lightboxPanel) return []
      return Array.from(this.lightboxPanel.querySelectorAll('a[href], button:not([disabled])'))
        .filter((node) => node.offsetParent !== null)
    }

    onLightboxKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.closeLightbox()
        return
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.stepLightbox(-1)
        return
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.stepLightbox(1)
        return
      }
      if (event.key !== 'Tab') return
      const focusable = this.lightboxFocusable()
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    /* ----------------------------------------------------------------- crypt */
    wireCrypt() {
      this.openCryptButton?.addEventListener('click', () => this.openCrypt())
      this.logoutLink?.addEventListener('click', (event) => this.logoutMember(event))
    }

    /* REVISION 3, ITEM B — the crypt is the gate's second door.

       The client found that "Open the crypt" raised the membership gate and
       liked it, so it is deliberate now instead of incidental. The rules are
       exactly the hero gate's rules, which is the point — one question, asked
       once:

         · signed-in members            → never asked (they never see the
                                          control at all; guarded twice anyway)
         · UTM member-view arrivals     → never asked
         · already answered this session→ never asked again, either door
         · everyone else                → "Who goes there?", with the member
                                          branch returning to #nosferatu-membership

       revealCrypt() is the old openCrypt() body, unchanged, so the gate is a
       pure precondition and nothing about opening the crypt moved. */
    openCrypt() {
      if (this.memberAuthorized) return
      if (!this.memberView && !this.gateIsAnswered()) {
        this.openGate('crypt')
        return
      }
      this.revealCrypt()
    }

    revealCrypt() {
      // Commerce gate: only a non-authorized visitor ever sees this control,
      // and it is re-checked here rather than trusted from the DOM.
      if (this.memberAuthorized) return
      this.dockBuyBox()
      this.classList.add('is-crypt-open')
      this.card?.classList.add('is-open')
      this.querySelector('[data-nos-card-back]')?.setAttribute('aria-hidden', 'false')
      this.openCryptButton?.setAttribute('aria-expanded', 'true')
      this.playDoorCreak()
      this.announce('The crypt is open. Membership options are now available.')
      window.setTimeout(() => {
        this.querySelector('[data-nos-card-back]')?.scrollIntoView({
          behavior: this.prefersReducedMotion || !this.motionAllowed ? 'auto' : 'smooth',
          block: 'start'
        })
        this.buyBox?.querySelector('select, input:not([type="hidden"]), button')?.focus({ preventScroll: true })
      }, this.prefersReducedMotion || !this.motionAllowed ? 0 : 700)
    }

    /* The real featured-product Loop form lives in its own template section.
       For prospects it is physically relocated into the crypt card. */
    findBuyBox() {
      const key = this.dataset.buyBoxKey || 'nosferatu_buy_box'
      return document.querySelector(`.shopify-section[id$="__${key}"]`)
    }

    dockBuyBox() {
      if (this.memberAuthorized) return
      this.buyBox = this.buyBox || this.findBuyBox()
      if (!this.buyBox) {
        /* Revision 4, item 6 made this the one failure mode worth naming out
           loud. The section wrapper is parked off-screen until docking adds
           `.nos-buybox--revealed`, so a mis-typed `buy_box_section_key` no
           longer shows a second buy box — it shows none. That is the right
           trade (the client asked for exactly one purchase UI) but it must be
           loud in QA rather than silent. */
        if (!window.Shopify?.designMode) {
          console.warn('[nosferatu] buy box section not found for key:', this.dataset.buyBoxKey)
        }
        return
      }
      if (!this.buyBoxSlot || window.Shopify?.designMode) return
      if (this.buyBoxSlot.contains(this.buyBox)) return
      this.buyBoxSlot.append(this.buyBox)
      this.buyBox.classList.add('nos-buybox--revealed')
    }

    /* Belt and braces only. The authoritative fix is the server-side guard in
       featured-product.liquid documented in notes/build-core-section.md — this
       cannot render a form that was never emitted, it only removes a form the
       template emitted before that guard lands. */
    hideProspectBuyBox() {
      if (window.Shopify?.designMode) return
      this.findBuyBox()?.remove()
    }

    async logoutMember(event) {
      event.preventDefault()
      if (!this.logoutLink) return
      this.announce('Signing out so a gift can be purchased from another account.')
      try {
        await fetch(this.logoutLink.href, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          redirect: 'follow'
        })
      } catch (error) {
        // The navigation below stays the reliable fallback.
      }
      window.location.assign(`${window.location.pathname}?show_join=1#nosferatu-membership`)
    }

    /* ------------------------------------------------------- scenes / scroll */
    observeScenes() {
      this.sceneObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible')
          if (entry.target === this.cryptSection) {
            this.cryptVisible = entry.isIntersecting
            if (entry.isIntersecting) {
              this.startHeartbeat()
            } else {
              this.stopHeartbeat()
            }
          }
        })
        this.syncLoops()
      }, { threshold: 0.14, rootMargin: '0px 0px -8% 0px' })

      this.reveals.forEach((node) => this.sceneObserver.observe(node))
      if (this.cryptSection && !this.reveals.includes(this.cryptSection)) {
        this.sceneObserver.observe(this.cryptSection)
      }
    }

    syncLoops() {
      if (!this.loops.length) return
      const allowed = this.motionAllowed && !this.prefersReducedMotion
      this.loops.forEach((video) => {
        const rect = video.getBoundingClientRect()
        const onScreen = rect.bottom > 0 && rect.top < window.innerHeight
        if (allowed && onScreen) {
          const attempt = video.play()
          if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {})
        } else {
          video.pause()
        }
      })
    }

    onScroll() {
      if (this.scrollTicking) return
      this.scrollTicking = true
      window.requestAnimationFrame(() => {
        const rect = this.getBoundingClientRect()
        const total = Math.max(1, rect.height - window.innerHeight)
        const progress = Math.min(1, Math.max(0, -rect.top / total))
        this.style.setProperty('--nos-progress', `${(progress * 100).toFixed(2)}%`)
        if (this.ascentReadout) this.ascentReadout.textContent = `${Math.round(progress * 100)}%`
        if (this.motionAllowed && !this.prefersReducedMotion) {
          const heroProgress = Math.min(1, Math.max(0, -rect.top / Math.max(1, window.innerHeight)))
          this.style.setProperty('--nos-scroll', String(heroProgress.toFixed(3)))
        }
        this.scrollTicking = false
      })
    }
  }

  if (!customElements.get('nosferatu-story')) customElements.define('nosferatu-story', NosferatuStory)
